import axios from 'axios';
import { Timestamps } from '../../types/timestamps';

// ASLV is served by the legacy per-book timestamp endpoint, while every other
// translation uses the per-video endpoint keyed by the gateway video id.
const PER_BOOK_TRANSLATIONS = ['aslv'];

const perBookUrl = (translationCode: string, bookCode: string): string =>
  `${process.env.REACT_APP_NEW_API}/translations/${translationCode}/${bookCode}/timestamp`;

const perVideoUrl = (translationCode: string, bookCode: string, videoId: string): string =>
  `${process.env.REACT_APP_NEW_API}/translations/${translationCode}/${bookCode}/${videoId}/timestamp`;

const segmentsIncludeChapter = (records: Timestamps[], chapter: number): boolean =>
  records.some(record => record.seg?.some(segment => segment.c === chapter));

const filterBookRecords = (
  bookData: Timestamps[] | undefined,
  bbbId: string | undefined
): Timestamps[] => {
  if (!Array.isArray(bookData)) {
    return [];
  }

  if (bbbId === undefined) {
    return bookData;
  }

  return bookData.filter(record => record.id === bbbId);
};

/**
 * Fetches verse markers for a video.
 *
 * Prefers the per-video endpoint, then falls back to the per-book endpoint when
 * the per-video payload is empty or its segment chapters do not match the video
 * (e.g. KOBBB Mark 8 / Genesis 4 labeled as chapter 1).
 */
const getTimestamps = async (
  translationCode: string | undefined,
  bookCode: string | undefined,
  videoId: string | undefined,
  bbbId?: string | undefined,
  expectedChapter?: number | undefined
): Promise<Timestamps[]> => {
  if (translationCode === undefined || bookCode === undefined) {
    return [];
  }

  const usePerBook = PER_BOOK_TRANSLATIONS.includes(translationCode.toLowerCase());

  if (usePerBook) {
    const { data } = await axios.get(perBookUrl(translationCode, bookCode));
    return data;
  }

  if (videoId === undefined) {
    return [];
  }

  const { data } = await axios.get(perVideoUrl(translationCode, bookCode, videoId));

  const hasExpectedChapter =
    expectedChapter === undefined ||
    Number.isNaN(expectedChapter) ||
    (Array.isArray(data) && segmentsIncludeChapter(data, expectedChapter));

  if (Array.isArray(data) && data.length > 0 && hasExpectedChapter) {
    return data;
  }

  // Empty per-video payload (e.g. KOBBB Mark 15:1-20) or wrong chapter labels
  // on segments — use the matching per-book record instead.
  const { data: bookData } = await axios.get(perBookUrl(translationCode, bookCode));

  return filterBookRecords(bookData, bbbId);
};

export default getTimestamps;
